import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Spinner } from "@/components/States";
import { motion } from "framer-motion";
import {
  ChevronLeft, MapPin, Phone, Mail, Globe, MessageCircle,
  Facebook, Instagram, Navigation, Tag, Share2, Star,
} from "lucide-react";

const TABS = ["Descripción", "Contacto", "Ubicación"];

function SocialBtn({ href, icon: Icon, label, color, testid }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="flex flex-col items-center gap-1.5"
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-2xl border border-black/5"
        style={{ backgroundColor: `${color}14` }}
      >
        <Icon size={20} style={{ color }} strokeWidth={2.2} />
      </div>
      <span className="text-[10px] font-bold text-[#5C5C5C]">{label}</span>
    </a>
  );
}

// TikTok isn't in lucide; small inline glyph.
function TikTokBtn({ href }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="social-tiktok" className="flex flex-col items-center gap-1.5">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-black/5 bg-[#1A1A1A]/5">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#1A1A1A"><path d="M16.5 3c.3 2 1.6 3.6 3.5 3.9v2.6c-1.3 0-2.5-.4-3.5-1v6.1c0 3-2.4 5.4-5.4 5.4S5.7 17.6 5.7 14.6s2.4-5.4 5.4-5.4c.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.5 0-2.7 1.2-2.7 2.8s1.2 2.7 2.7 2.7 2.8-1.2 2.8-2.7V3h2.6z"/></svg>
      </div>
      <span className="text-[10px] font-bold text-[#5C5C5C]">TikTok</span>
    </a>
  );
}

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCategory } = useData();
  const [biz, setBiz] = useState(null);
  const [tab, setTab] = useState("Descripción");

  useEffect(() => {
    api.get(`/businesses/${id}`).then((r) => setBiz(r.data)).catch(() => setBiz(false));
  }, [id]);

  if (biz === null)
    return <div className="mx-auto min-h-screen max-w-md bg-[#FDFBF7]"><Spinner /></div>;
  if (biz === false)
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center bg-[#FDFBF7]">
        <p className="font-display text-xl font-bold">Negocio no encontrado</p>
        <button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#1A1A1A] px-5 py-2 text-sm font-bold text-white">Volver</button>
      </div>
    );

  const cat = getCategory(biz.category);
  const CatIcon = cat.icon;
  const d = biz.discount || {};
  const wa = biz.whatsapp ? `https://wa.me/${biz.whatsapp.replace(/\D/g, "")}` : "";
  const mapUrl = biz.google_maps_url ||
    (biz.lat && biz.lng ? `https://maps.google.com/?q=${biz.lat},${biz.lng}` : `https://maps.google.com/?q=${encodeURIComponent(biz.full_address || biz.name)}`);
  const gallery = [biz.hero_image_url, ...(biz.gallery || [])].filter(Boolean);
  const hasPromo = !!(d.title || d.badge);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${biz.name}${hasPromo ? ` — ${d.title || d.badge}` : ""} · Saipass`;

  const doShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: biz.name, text: shareText, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      const waShare = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
      window.open(waShare, "_blank");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado");
    } catch (e) {
      toast.error("No se pudo copiar el enlace");
    }
  };

  return (
    <div className="relative mx-auto min-h-screen w-full max-w-md bg-[#FDFBF7]">
      {/* Hero */}
      <div className="relative h-[42vh] w-full overflow-hidden bg-[#F3F0E6]">
        {biz.hero_image_url && (
          <img src={imgUrl(biz.hero_image_url)} alt={biz.name} className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
        <button
          onClick={() => navigate(-1)}
          data-testid="detail-back"
          className="absolute left-5 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={doShare}
          data-testid="share-button"
          className="absolute right-5 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur"
        >
          <Share2 size={18} />
        </button>
        {biz.featured && (
          <div className="absolute left-5 top-20 flex items-center gap-1 rounded-full bg-[#1A1A1A] px-3 py-1 text-[11px] font-black uppercase text-white shadow-lg">
            <Star size={12} className="fill-[#FFB800] text-[#FFB800]" /> Destacado
          </div>
        )}
      </div>

      {/* Content sheet */}
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="relative -mt-8 rounded-t-3xl bg-[#FDFBF7] px-5 pb-32 pt-6"
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <CatIcon size={15} style={{ color: cat.color }} strokeWidth={2.4} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: cat.color }}>{cat.label}</span>
          </div>
          {biz.membership && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-white"
              style={{ backgroundColor: biz.membership.color }}
              data-testid="membership-badge"
            >
              {biz.membership.badge_label || biz.membership.name}
            </span>
          )}
        </div>
        <h1 className="mt-1 font-display text-3xl font-black leading-tight tracking-tight text-[#1A1A1A]">
          {biz.name}
        </h1>
        <p className="mt-1 flex items-center gap-1 text-sm text-[#5C5C5C]">
          <MapPin size={14} /> {biz.colonia ? `${biz.colonia}, ` : ""}{biz.city}, {biz.state}
        </p>

        {/* Prominent discount / benefit section */}
        {hasPromo && (
          <div
            className="grain mt-4 flex items-center gap-4 overflow-hidden rounded-2xl p-4 text-white shadow-lg shadow-[#E54D2E]/20"
            style={{ background: "linear-gradient(135deg, #E54D2E, #FF8C42)" }}
            data-testid="discount-highlight"
          >
            {d.badge && (
              <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-white/20 text-center backdrop-blur">
                <span className="font-display text-lg font-black leading-none">{d.badge}</span>
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <Tag size={14} />
                <p className="text-[11px] font-bold uppercase tracking-widest">Beneficio exclusivo</p>
              </div>
              <p className="mt-0.5 font-display text-lg font-black leading-tight">{d.title || "Promoción especial"}</p>
              {d.description && <p className="text-sm opacity-90">{d.description}</p>}
            </div>
          </div>
        )}

        {/* Quick actions: WhatsApp + Llamar + Compartir */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {wa && (
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              className="flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] py-3.5 font-bold text-white shadow-lg shadow-[#25D366]/25"
            >
              <MessageCircle size={18} strokeWidth={2.4} /> WhatsApp
            </a>
          )}
          {biz.phone && (
            <a
              href={`tel:${biz.phone}`}
              data-testid="call-cta"
              className="flex items-center justify-center gap-2 rounded-2xl bg-[#2B4C3B] py-3.5 font-bold text-white shadow-lg shadow-[#2B4C3B]/20"
            >
              <Phone size={18} strokeWidth={2.4} /> Llamar
            </a>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button
            onClick={doShare}
            data-testid="share-cta"
            className="flex items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white py-3 font-bold text-[#1A1A1A]"
          >
            <Share2 size={17} strokeWidth={2.2} /> Compartir
          </button>
          <button
            onClick={copyLink}
            data-testid="copy-link-cta"
            className="flex items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white py-3 font-bold text-[#1A1A1A]"
          >
            <Globe size={17} strokeWidth={2.2} /> Copiar enlace
          </button>
        </div>

        {/* Gallery */}
        {gallery.length > 1 && (
          <div className="mt-6" data-testid="gallery">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]">Galería</p>
            <div className="no-scrollbar -mx-5 mt-3 flex gap-3 overflow-x-auto px-5">
              {gallery.map((g, i) => (
                <img
                  key={i}
                  src={imgUrl(g)}
                  alt={`${biz.name} ${i + 1}`}
                  data-testid={`gallery-img-${i}`}
                  className="h-40 w-56 shrink-0 rounded-2xl border border-black/5 object-cover"
                />
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-6 flex gap-2 rounded-full bg-[#F3F0E6] p-1">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${
                tab === t ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#5C5C5C]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "Descripción" && (
            <p className="text-[15px] leading-relaxed text-[#5C5C5C]" data-testid="tab-content-description">
              {biz.description || "Este negocio aún no tiene descripción."}
            </p>
          )}

          {tab === "Contacto" && (
            <div data-testid="tab-content-contact">
              <div className="flex flex-col gap-2">
                {biz.phone && (
                  <a href={`tel:${biz.phone}`} data-testid="contact-phone" className="flex items-center gap-3 rounded-xl border border-black/5 bg-white px-4 py-3">
                    <Phone size={18} className="text-[#2B4C3B]" /><span className="font-semibold">{biz.phone}</span>
                  </a>
                )}
                {biz.email && (
                  <a href={`mailto:${biz.email}`} data-testid="contact-email" className="flex items-center gap-3 rounded-xl border border-black/5 bg-white px-4 py-3">
                    <Mail size={18} className="text-[#2B4C3B]" /><span className="font-semibold">{biz.email}</span>
                  </a>
                )}
              </div>
              <div className="mt-5 flex flex-wrap gap-4">
                <SocialBtn href={wa} icon={MessageCircle} label="WhatsApp" color="#25D366" testid="social-whatsapp" />
                <SocialBtn href={biz.facebook} icon={Facebook} label="Facebook" color="#1877F2" testid="social-facebook" />
                <SocialBtn href={biz.instagram} icon={Instagram} label="Instagram" color="#E4405F" testid="social-instagram" />
                <TikTokBtn href={biz.tiktok} />
                <SocialBtn href={biz.website} icon={Globe} label="Web" color="#0891B2" testid="social-website" />
              </div>
            </div>
          )}

          {tab === "Ubicación" && (
            <div data-testid="tab-content-location">
              <div className="rounded-xl border border-black/5 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C]">Dirección</p>
                <p className="mt-1 font-semibold text-[#1A1A1A]">
                  {biz.full_address || `${biz.street}, ${biz.colonia}, ${biz.city}, ${biz.state}`}
                </p>
                {biz.postal_code && <p className="text-sm text-[#5C5C5C]">C.P. {biz.postal_code}</p>}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Sticky Google Maps CTA */}
      <div className="fixed bottom-0 left-1/2 z-50 w-full max-w-md -translate-x-1/2 glass border-t border-black/5 px-5 py-3 pb-safe">
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="map-cta"
          className="flex items-center justify-center gap-2 rounded-2xl bg-[#1A1A1A] py-4 font-bold text-white"
        >
          <Navigation size={18} strokeWidth={2.4} /> Abrir en Google Maps
        </a>
      </div>
    </div>
  );
}
